import { type EditorState } from '@tiptap/pm/state';
import { type EditorView } from '@tiptap/pm/view';
import { type Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { type ReactNode } from 'react';
import { getSelectionStartAnchor } from '../core';
import { getDialogContainer } from './get-dialog-container';
import { Toolbar } from './toolbar-namespace';

export interface TextMenuWrapperProps {
  editor: Editor;
  shouldShow: (props: { view: EditorView; state: EditorState; from: number }) => boolean;
  /**
   * Host-controlled visibility gate (design D5's "toolbar visibility"
   * concrete option), folded into `shouldShow`. The app wrapper at the legacy
   * path wires this to its `useIsMobile()` — a debounced-resize breakpoint
   * with documented quirks that deliberately stays app infrastructure (the
   * coding package avoided duplicating it for the same reasons). Undefined
   * means never suppressed.
   */
  suppressed?: boolean;
  /** Overrides the default dialog-aware append target (the editor's parent). */
  appendTo?: () => HTMLElement;
  /**
   * Scroll container that repositions the menu. BubbleMenu reads it once at
   * plugin registration and defaults to `window`, so pass it on first mount.
   */
  scrollTarget?: HTMLElement | Window;
  children: ReactNode;
}

/**
 * The bubble-menu frame every text menu renders through (6.5/6.6 wave B-1):
 * selection-anchored, dialog-aware placement over the host editor, with the
 * shared toolbar chrome. Moved verbatim from the app's `text-menu-base` except
 * for the `suppressed` inversion documented above.
 */
export const TextMenuWrapper = ({
  editor,
  shouldShow,
  suppressed,
  appendTo,
  scrollTarget,
  children,
}: TextMenuWrapperProps) => (
  <BubbleMenu
    // Distinct from TableMenu: TipTap unregisterPlugin prefix-matches the key.
    pluginKey="textMenu"
    shouldShow={({ view, state, from }) =>
      shouldShow({ view, state, from }) && suppressed !== true
    }
    editor={editor}
    // TipTap forwards `className` to an unpositioned inner div where a bare
    // z-index is inert; `relative` makes z-50 apply over the canvas gutter (z-10).
    className="relative z-50"
    appendTo={appendTo ?? (() => getDialogContainer(editor))}
    getReferencedVirtualElement={() => getSelectionStartAnchor(editor)}
    // Outside the scroller the menu no longer scrolls natively; the default
    // debounce only repositions once scrolling stops, so it lags behind the text.
    resizeDelay={scrollTarget instanceof HTMLElement ? 0 : undefined}
    options={{
      placement: 'top-start',
      scrollTarget,
      // Appended outside the scroller, the menu is no longer clipped by it; the
      // selection anchor has no contextElement, so name the boundary explicitly.
      hide: scrollTarget instanceof HTMLElement ? { boundary: scrollTarget } : undefined,
    }}
  >
    <Toolbar.Wrapper
      // Buttons carry `transition-all`, which also animates the `visibility`
      // the hide middleware sets on the host — they'd linger after the surface.
      className="[&_*]:[transition-property:color,background-color,border-color,box-shadow,opacity,transform]"
      onMouseDown={(e) => {
        e.preventDefault();
      }}
    >
      {children}
    </Toolbar.Wrapper>
  </BubbleMenu>
);
